import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FilePlus2, FolderOpen, Boxes, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

const NAV = [
  { to: "/mup", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/mup/nuova", label: "Nuova pratica", icon: FilePlus2 },
  { to: "/mup/pratiche", label: "Pratiche", icon: FolderOpen },
  { to: "/mup/catalogo", label: "Catalogo & Compliance", icon: Boxes, adminOnly: true },
];

export const MupLayout = ({ children, title, subtitle, actions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 [.suite-shell_&]:top-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="mup-back-hub"
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-all"
            >
              <ArrowLeft className="h-4 w-4" /> Suite
            </button>
            <div className="h-5 w-px bg-slate-200" />
            <img src={LOGO_URL} alt="Assietica" className="h-11 w-auto object-contain [.suite-shell_&]:hidden" />
            <div className="hidden sm:block leading-tight">
              <div className="font-heading font-semibold text-slate-900">MUP Assietica</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Moduli Unici Precontrattuali</div>
            </div>
          </div>
          <span className="text-xs text-slate-500 hidden md:block">{user?.email}</span>
        </div>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
            const Icon = n.icon;
            const active = isActive(n);
            return (
              <button
                key={n.to}
                data-testid={`mup-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => navigate(n.to)}
                className={`flex items-center gap-2 px-3 py-2.5 text-sm border-b-2 transition-all whitespace-nowrap ${
                  active ? "border-brand-blue text-brand-blue font-medium" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <Icon className="h-4 w-4" /> {n.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {(title || actions) && (
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              {subtitle && <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">{subtitle}</p>}
              {title && <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-900 tracking-tight">{title}</h1>}
            </div>
            {actions}
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

export const STATE_META = {
  bozza: { label: "Bozza", cls: "bg-slate-100 text-slate-600" },
  pronto_invio: { label: "Pronto per invio", cls: "bg-blue-100 text-blue-700" },
  inviato: { label: "Inviato", cls: "bg-amber-100 text-amber-700" },
  firmato: { label: "Firmato", cls: "bg-emerald-100 text-emerald-700" },
  annullato: { label: "Annullato", cls: "bg-red-100 text-red-700" },
  da_revisionare: { label: "Da revisionare", cls: "bg-orange-100 text-orange-700" },
};

export const StateBadge = ({ state }) => {
  const m = STATE_META[state] || STATE_META.bozza;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.cls}`}>{m.label}</span>;
};
